import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, Users, UserCog, LogOut, ShieldCheck, Trophy, Shield, User, Swords } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import FbmLogo from "@/components/FbmLogo";

const navItems = [
  { to: "/admin/organizations", label: "Organizations", icon: Building2, testId: "nav-organizations" },
  { to: "/admin/scouts", label: "Scouts", icon: Users, testId: "nav-scouts" },
  { to: "/admin/leagues", label: "Leagues", icon: Trophy, testId: "nav-leagues" },
  { to: "/admin/teams", label: "Teams", icon: Shield, testId: "nav-teams" },
  { to: "/admin/players", label: "Players", icon: User, testId: "nav-players" },
  { to: "/admin/matches", label: "Matches", icon: Swords, testId: "nav-matches" },
  { to: "/admin/account", label: "My Account", icon: UserCog, testId: "nav-account" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="admin-theme min-h-screen bg-background text-foreground flex">
      <aside className="admin-sidebar w-64 shrink-0 flex flex-col" data-testid="admin-sidebar">
        <div className="px-6 py-7 border-b border-border/40">
          <div className="flex items-center gap-3">
            <FbmLogo size={36} withText={false} theme="dark" />
            <div>
              <div className="font-display uppercase tracking-[0.2em] text-[11px] text-white/60">FBM</div>
              <div className="font-display uppercase tracking-tight text-lg text-white">Admin</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md font-display uppercase tracking-[0.14em] text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-5 border-t border-border/40 space-y-3">
          <div className="flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <span className="uppercase tracking-[0.18em]">Admin</span>
          </div>
          <div className="text-sm text-white truncate" data-testid="admin-user-name">{user?.full_name}</div>
          <div className="text-xs text-white/50 truncate">{user?.email}</div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full h-10 rounded-none font-display uppercase tracking-[0.18em] text-xs bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 md:py-12">
          <Outlet />
        </div>
      </main>

      <Toaster richColors position="top-right" theme="dark" />
    </div>
  );
}
